import { cva } from 'class-variance-authority'

import { AccountsNavigationProps, ShortAccountDefinition } from '../../types'
import { HighYieldOpportunitiesTooltip } from './components/HighYieldOpportunitiesTooltip'
import { NavigationItem } from './components/NavigationItem'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/ui/atoms/collapsible/Collapsible'
import { ExternalLink } from '@/ui/atoms/link/Link'
import { links } from '@/ui/constants/links'
import { Info } from '@/ui/molecules/info/Info'
import { cn } from '@/ui/utils/style'
import { testIds } from '@/ui/utils/testIds'

export function AccountsNavigation({
  savingsAccounts,
  opportunityAccounts,
  legacyAccounts,
  legacyAccountsExpanded,
  selectedAccount,
}: AccountsNavigationProps) {
  return (
    <div
      className={cn(
        '-ml-5 -mr-5 flex overflow-x-auto pr-5 pl-5 xl:mr-0 xl:ml-0 xl:block xl:overflow-visible xl:pr-0 xl:pl-0',
        'mask-[linear-gradient(to_right,rgb(0,0,0,0.3),black_6%,black_94%,rgb(0,0,0,0.3))] xl:mask-none',
      )}
    >
      <AccountsNavigationComponent
        savingsAccounts={savingsAccounts}
        opportunityAccounts={opportunityAccounts}
        legacyAccounts={legacyAccounts}
        legacyAccountsExpanded={legacyAccountsExpanded}
        selectedAccount={selectedAccount}
        variant="vertical"
        className="hidden xl:block"
      />
      <AccountsNavigationComponent
        savingsAccounts={savingsAccounts}
        opportunityAccounts={opportunityAccounts}
        legacyAccounts={legacyAccounts}
        legacyAccountsExpanded={legacyAccountsExpanded}
        selectedAccount={selectedAccount}
        variant="horizontal"
        className="xl:hidden"
      />
    </div>
  )
}

interface AccountsNavigationComponentProps extends AccountsNavigationProps {
  variant: 'horizontal' | 'vertical'
  className?: string
}

export function AccountsNavigationComponent({
  savingsAccounts,
  opportunityAccounts,
  legacyAccounts,
  legacyAccountsExpanded,
  selectedAccount,
  variant,
  className,
}: AccountsNavigationComponentProps) {
  return (
    <div
      data-testid={testIds.savings.navigation.container}
      className={cn(accountsNavigationVariants({ variant }), className)}
    >
      <div className="flex flex-col">
        <SavingsAccountsHeader />
        <div className={accountsNavigationVariants({ variant })}>
          {savingsAccounts.map((account) => (
            <NavigationItem
              key={account.vaultToken.symbol}
              active={account.vaultToken.symbol === selectedAccount}
              variant={variant}
              {...account}
            />
          ))}
        </div>
      </div>
      {opportunityAccounts.length > 0 && (
        <div className={cn('flex flex-col', variant === 'vertical' ? 'mt-6' : 'ml-6')}>
          <div className="typography-label-4 mb-3 flex items-center gap-1 whitespace-nowrap text-secondary">
            Higher-yield Opportunities
            <HighYieldOpportunitiesTooltip />
          </div>
          <div className={accountsNavigationVariants({ variant })}>
            {opportunityAccounts.map((account) => (
              <NavigationItem
                key={account.vaultToken.symbol}
                active={account.vaultToken.symbol === selectedAccount}
                variant={variant}
                {...account}
              />
            ))}
          </div>
        </div>
      )}
      {legacyAccounts.length > 0 &&
        (variant === 'vertical' ? (
          <div className="mt-6 flex flex-col">
            <Collapsible defaultOpen={legacyAccountsExpanded}>
              <CollapsibleTrigger className="[&[data-state=open]>svg]:icon-secondary mb-3 [&>svg]:size-4">
                <LegacyAccountsHeader />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <LegacyAccounts legacyAccounts={legacyAccounts} selectedAccount={selectedAccount} variant={variant} />
              </CollapsibleContent>
            </Collapsible>
          </div>
        ) : (
          <div className="ml-6 flex flex-col">
            <LegacyAccountsHeader className="mb-3" />
            <LegacyAccounts legacyAccounts={legacyAccounts} selectedAccount={selectedAccount} variant={variant} />
          </div>
        ))}
    </div>
  )
}

function SavingsAccountsHeader() {
  return (
    <div className="typography-label-4 mb-3 flex items-center gap-1 whitespace-nowrap text-secondary">
      Savings accounts
      <Info className="icon-tertiary icon-size-xxs hover:icon-brand-primary">
        Savings v2 vaults allocate funds through the Spark Liquidity Layer and offer rewards based on the Spark
        Universal Savings Rate (SUSR), with on-demand liquidity.{' '}
        {import.meta.env.VITE_BASE_MINI_APP !== '1' && (
          <ExternalLink to={links.docs.savings.earningSavings} variant="secondary">
            Learn more
          </ExternalLink>
        )}
      </Info>
    </div>
  )
}

function LegacyAccountsHeader({ className }: { className?: string }) {
  return (
    <div className={cn('typography-label-4 flex items-center gap-1 whitespace-nowrap text-secondary', className)}>
      Legacy accounts
      <Info className="icon-tertiary icon-size-xxs hover:icon-brand-primary">
        Legacy Savings vaults use the previous version of the system, where funds were allocated to Sky's sUSDS. They
        remain active but may offer lower rewards.{' '}
        {import.meta.env.VITE_BASE_MINI_APP !== '1' && (
          <ExternalLink to={links.docs.savings.legacy} variant="secondary">
            Learn more
          </ExternalLink>
        )}
      </Info>
    </div>
  )
}

interface LegacyAccountsProps {
  legacyAccounts: ShortAccountDefinition[]
  selectedAccount: TokenSymbol
  variant: 'horizontal' | 'vertical'
}

function LegacyAccounts({ legacyAccounts, selectedAccount, variant }: LegacyAccountsProps) {
  return (
    <div className={accountsNavigationVariants({ variant })}>
      {legacyAccounts.map((account) => (
        <NavigationItem
          key={account.vaultToken.symbol}
          active={account.vaultToken.symbol === selectedAccount}
          variant={variant}
          {...account}
        />
      ))}
    </div>
  )
}

const accountsNavigationVariants = cva('', {
  variants: {
    variant: {
      horizontal: 'flex',
      vertical: 'flex-col',
    },
  },
})
